<template>
  <NativeDrawer
    v-model="drawerVisible"
    :title="isEdit ? '编辑专家' : '新增专家'"
    size="600px"
    direction="rtl"
    @close="handleClose"
    destroy-on-close
    :before-close="handleDrawerClose"
  >
    <div class="drawer-content">
      <NativeForm
        ref="formRef"
        :model="localFormData"
        :rules="formRules"
        label-width="90px"
        class="doctor-form"
      >
        <!-- 基本信息 -->
        <div class="form-section">
          <div class="section-title">
            <span class="native-icon" aria-hidden="true">•</span>
            <span>基本信息</span>
          </div>

          <NativeRow :gutter="20">
            <NativeCol :span="12">
              <NativeFormItem label="姓名" prop="name">
                <NativeInput
                  v-model="localFormData.name"
                  placeholder="请输入姓名"
                  clearable
                />
              </NativeFormItem>
            </NativeCol>
            <NativeCol :span="12">
              <NativeFormItem label="职称" prop="title">
                <NativeInput
                  v-model="localFormData.title"
                  placeholder="请输入职称"
                  clearable
                />
              </NativeFormItem>
            </NativeCol>
          </NativeRow>

          <NativeRow :gutter="20">
            <NativeCol :span="12">
              <NativeFormItem label="类型" prop="type">
                <NativeSelect
                  v-model="localFormData.type"
                  placeholder="请选择类型"
                  style="width: 100%"
                  clearable
                >
                  <NativeOption label="门诊" :value="1" />
                  <NativeOption label="住院" :value="2" />
                </NativeSelect>
              </NativeFormItem> </NativeCol><NativeCol :span="12">
              <NativeFormItem label="标签" prop="badge">
                <NativeInput
                  v-model="localFormData.badge"
                  placeholder="请输入标签"
                  clearable
                />
              </NativeFormItem>
            </NativeCol>
          </NativeRow>

          <NativeRow :gutter="20">
            <NativeCol :span="12">
              <NativeFormItem label="科室" prop="specialty">
                <NativeSelect
                  v-model="localFormData.specialty"
                  placeholder="请选择科室"
                  style="width: 100%"
                  clearable
                >
                  <NativeOption
                    v-for="item in dept"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </NativeSelect>
              </NativeFormItem>
            </NativeCol>
            <NativeCol :span="12">
              <NativeFormItem label="类别" prop="category">
                <NativeInput
                  v-model="localFormData.category"
                  placeholder="请输入类别"
                  :disabled="true"
                />
              </NativeFormItem>
            </NativeCol>
          </NativeRow>

          <NativeRow :gutter="20">
            <NativeCol :span="12">
              <NativeFormItem label="首页展示" prop="major">
                <NativeSwitch
                  v-model="localFormData.major"
                  active-text="是"
                  inactive-text="否"
                />
              </NativeFormItem>
            </NativeCol>
          </NativeRow>
        </div>

        <!-- 详细信息 -->
        <div class="form-section">
          <div class="section-title">
            <span class="native-icon" aria-hidden="true">•</span>
            <span>详细信息</span>
          </div>
          <NativeFormItem label="简介" prop="experience">
            <NativeInput
              v-model="localFormData.experience"
              type="textarea"
              :rows="4"
              placeholder="请输入专家简介，建议10-500字"
              maxlength="500"
              show-word-limit
            />
          </NativeFormItem>
        </div>

        <!-- 擅长领域 -->
        <div class="form-section">
          <div class="section-title">
            <span class="native-icon" aria-hidden="true">•</span>
            <span>擅长领域</span>
          </div>

          <NativeFormItem label="" class="expertise-list">
            <div
              v-for="(item, index) in localFormData.expertiseList || []"
              :key="index"
              class="expertise-item"
            >
              <div class="expertise-input-wrapper">
                <NativeInput
                  v-model="item.expertise"
                  placeholder="请输入擅长领域"
                />
              </div>
              <NativeButton
                type="danger"
                size="small"
                
                circle
                @click="removeExpertise(index)"
                :disabled="(localFormData.expertiseList?.length || 0) <= 1"
                class="delete-btn"
              >
                ×
              </NativeButton>
            </div>

            <NativeButton
              type="primary"
              size="default"
              @click="addExpertise"
              class="add-btn"
            >
              <span class="native-icon" aria-hidden="true">+</span>
              <span>添加擅长领域</span>
            </NativeButton>
          </NativeFormItem>
        </div>
      </NativeForm>
    </div>

    <template #footer>
      <div class="drawer-footer">
        <NativeButton @click="handleCancel" size="default">
          <span class="native-icon" aria-hidden="true">×</span>
          <span>取消</span>
        </NativeButton>
        <NativeButton
          type="primary"
          @click="handleSubmit"
          :loading="submitLoading"
          size="default"
        >
          <span class="native-icon" aria-hidden="true">✓</span>
          <span>确定</span>
        </NativeButton>
      </div>
    </template>
  </NativeDrawer>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import type {
  NativeFormInstance,
  NativeFormRules,
} from "@/components/NativeUI/types";
import type { Doctor as DoctorType } from "@/stores/doctors";

// Props
const props = defineProps<{
  visible: boolean;
  isEdit: boolean;
  formData: Partial<DoctorType>;
  submitLoading: boolean;
}>();

// Emits
const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
  (
    e: "submit",
    data: {
      formData: Partial<DoctorType>; // 变更后的数据
      originalData: Partial<DoctorType>; // 原始数据
    },
  ): void;
  (e: "cancel"): void;
}>();

// 表单引用
const formRef = ref<NativeFormInstance>();

// Drawer 显示状态
const drawerVisible = ref(props.visible);

// 原始数据快照(用于对比修改)
const originalData = ref<Partial<DoctorType>>({});

// 本地表单数据(避免直接修改 props)
const localFormData = ref<Partial<DoctorType>>({
  id: 0,
  name: "",
  title: "",
  specialty: "",
  experience: "",
  badge: "",
  expertiseList: [{ expertise: "", sortOrder: 1 }],
  major: true,
  type: 0,
  avatar: "",
  category: "",
});

const deptMap = ref<Record<string, string>>(
  JSON.parse(localStorage.getItem("deptMap") || "{}"),
);
const dept = Object.keys(deptMap.value).map((item) => {
  return {
    label: item,
    value: item,
  };
});

// 同步表单数据的函数
const syncFormData = () => {
  // 深拷贝原始数据作为快照
  originalData.value = JSON.parse(JSON.stringify(props.formData));

  // 同步到本地表单数据
  localFormData.value = {
    ...props.formData,
    expertiseList:
      props.formData.expertiseList && props.formData.expertiseList.length > 0
        ? JSON.parse(JSON.stringify(props.formData.expertiseList)) // 深拷贝数组
        : [{ expertise: "", sortOrder: 0 }],
  };
};

watch(
  () => localFormData.value.specialty,
  (newVal) => {
    localFormData.value.category = deptMap.value[newVal as string] || "";
  },
);

// 监听 visible 变化
watch(
  () => props.visible,
  (newVal) => {
    drawerVisible.value = newVal;
    // Drawer 打开时同步数据
    if (newVal) {
      syncFormData();
    }
  },
);

// 监听 isEdit 变化,重新同步数据
watch(
  () => props.isEdit,
  () => {
    if (drawerVisible.value) {
      syncFormData();
    }
  },
);

// 监听 drawerVisible 变化
watch(drawerVisible, (newVal) => {
  emit("update:visible", newVal);
});

// 表单验证规则
const formRules: NativeFormRules = {
  name: [
    { required: true, message: "请输入姓名", trigger: "blur" },
    {
      validator: (_rule, value, callback) => {
        if (!value) {
          return callback(); 
        }

        const trimmedValue = value.trim();

        // 检验前后是否有空格
        if (value !== trimmedValue) {
          return callback(new Error("姓名前后不能包含空格"));
        }

        // 检验长度
        if (trimmedValue.length < 2 || trimmedValue.length > 10) {
          return callback(new Error("姓名长度为2-10个字符"));
        }

        // 检验是否全为中文
        if (!/^[\u4e00-\u9fa5]+$/.test(trimmedValue)) {
          return callback(new Error("姓名只能输入中文"));
        }

        callback();
      },
      trigger: "blur",
    },
  ],
  title: [
    { required: true, message: "请输入职称", trigger: "blur" },
    {
      validator: (_rule, value, callback) => {
        if (!value) {
          return callback(); 
        }

        const trimmedValue = value.trim();

        // 检验前后是否有空格
        if (value !== trimmedValue) {
          return callback(new Error("职称前后不能包含空格"));
        }

        // 检验长度
        if (trimmedValue.length < 2 || trimmedValue.length > 10) {
          return callback(new Error("职称长度为2-10个字符"));
        }

        // 检验是否全为中文
        if (!/^[\u4e00-\u9fa5]+$/.test(trimmedValue)) {
          return callback(new Error("职称只能输入中文"));
        }

        callback();
      },
      trigger: "blur",
    },
  ],
  specialty: [{ required: true, message: "请输入科室", trigger: "blur" }],
  type: [{ required: true, message: "请选择类型", trigger: "change" }],
  experience: [
    { required: true, message: "请输入简介", trigger: "blur" },
    { min: 10, message: "简介至少10个字符", trigger: "blur" },
  ],
};

// 添加擅长领域
const addExpertise = () => {
  if (!localFormData.value.expertiseList) {
    localFormData.value.expertiseList = [];
  }
  localFormData.value.expertiseList.push({
    expertise: "",
    sortOrder: localFormData.value.expertiseList.length,
  });
};

// 删除擅长领域
const removeExpertise = (index: number) => {
  if (
    localFormData.value.expertiseList &&
    localFormData.value.expertiseList.length > 1
  ) {
    localFormData.value.expertiseList.splice(index, 1);
    // 重新排序
    localFormData.value.expertiseList.forEach((item, idx) => {
      item.sortOrder = idx;
    });
  }
};

// 提交
const handleSubmit = async () => {
  if (!formRef.value) return;

  await formRef.value.validate((valid) => {
    if (valid) {
      // 过滤掉空的擅长领域
      const data = { ...localFormData.value };
      if (data.expertiseList) {
        data.expertiseList = data.expertiseList.filter((item) =>
          item.expertise.trim(),
        );
      }
      data.avatar = `/Doctor/${data.specialty}/${data.name}.jpg`;

      // 提交原始数据和变更后的数据
      emit("submit", {
        formData: data,
        originalData: originalData.value,
      });
    }
  });
};
const handleDrawerClose = (done: () => void) => {
  if (window.confirm("确认是否关闭?")) done();
};

// 取消
const handleCancel = () => {
  // 重置原始数据快照
  originalData.value = {};
  emit("cancel");
  drawerVisible.value = false;
};

// 关闭抽屉
const handleClose = () => {
  // 重置原始数据快照
  originalData.value = {};
  emit("cancel");
};
</script>

<style scoped lang="scss">
.drawer-content {
  height: calc(100% - 60px); // 减去 footer 高度
  overflow-y: auto;
  padding-right: 10px;

  // 自定义滚动条样式
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #dcdfe6;
    border-radius: 3px;

    &:hover {
      background-color: #c0c4cc;
    }
  }
}

.doctor-form {
  padding: 0 10px;

  .form-section {
    margin-bottom: 24px;
    padding: 16px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;

    &:last-child {
      margin-bottom: 0;
    }

    .section-title {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 16px;
      padding-bottom: 12px;
      border-bottom: 2px solid #dee2e6;
      font-size: 15px;
      font-weight: 600;
      color: #343a40;

      .native-icon {
        font-size: 18px;
        color: #409eff;
      }
    }
  }

  .expertise-list {
    margin-bottom: 0;

    .expertise-item {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;
      padding-right: 4px; // 为删除按钮预留空间

      &:last-child {
        margin-bottom: 0;
      }

      .expertise-input-wrapper {
        flex: 1;
        min-width: 0;

        .expertise-input {
          width: 100%;

          // 优化清除图标样式和位置
          :deep(input) {
            padding-right: 30px; // 为清除图标预留足够空间
          }
        }
      }

      .delete-btn {
        flex-shrink: 0;
        transition: all 0.3s;

        &:hover {
          transform: scale(1.1);
        }
      }
    }

    .add-btn {
      margin-top: 12px;
      width: 100%;
    }
  }
}

.drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 8px 0;
}

// 优化表单项间距
.native-form-item {
  margin-bottom: 18px;
}
</style>
